import React, { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import { IMAGE_URL } from "../utils/app_url";

const MODEL_OPTIONS = [
  { value: "none", label: "No model", langKey: "ai_model_none" },
  { value: "female", label: "Female model", langKey: "ai_model_female" },
  { value: "male", label: "Male model", langKey: "ai_model_male" },
  { value: "flat_lay", label: "Flat lay", langKey: "ai_model_flat_lay" },
  { value: "ghost_mannequin", label: "Ghost mannequin", langKey: "ai_model_ghost" },
];

const BG_OPTIONS = [
  { value: "white", label: "White studio", langKey: "ai_bg_white" },
  { value: "light_gray", label: "Light gray", langKey: "ai_bg_light_gray" },
  { value: "studio", label: "Studio", langKey: "ai_bg_studio" },
  { value: "outdoor", label: "Outdoor", langKey: "ai_bg_outdoor" },
  { value: "lifestyle", label: "Lifestyle", langKey: "ai_bg_lifestyle" },
  { value: "transparent", label: "Transparent", langKey: "ai_bg_transparent" },
];

const SCENE_OPTIONS = [
  { value: "random", label: "Random", langKey: "ai_scene_random" },
  { value: "street", label: "Street", langKey: "ai_scene_street" },
  { value: "bedroom", label: "Bedroom", langKey: "ai_scene_bedroom" },
  { value: "sunset", label: "Sunset", langKey: "ai_scene_sunset" },
  { value: "factory", label: "Factory", langKey: "ai_scene_factory" },
  { value: "studio", label: "Studio", langKey: "ai_bg_studio" },
  { value: "coloredstudio", label: "Colored studio", langKey: "ai_scene_coloredstudio" },
  { value: "concretestudio", label: "Concrete studio", langKey: "ai_scene_concretestudio" },
  { value: "beach", label: "Beach", langKey: "ai_scene_beach" },
  { value: "tropical", label: "Tropical", langKey: "ai_scene_tropical" },
  { value: "library", label: "Library", langKey: "ai_scene_library" },
  { value: "forest", label: "Forest", langKey: "ai_scene_forest" },
  { value: "businessdistrict", label: "Business district", langKey: "ai_scene_businessdistrict" },
  { value: "countryside", label: "Countryside", langKey: "ai_scene_countryside" },
  { value: "flowers", label: "Flowers", langKey: "ai_scene_flowers" },
  { value: "goldenlight", label: "Golden light", langKey: "ai_scene_goldenlight" },
  { value: "mountain", label: "Mountain", langKey: "ai_scene_mountain" },
  { value: "pool", label: "Pool", langKey: "ai_scene_pool" },
  { value: "latincity", label: "Latin city", langKey: "ai_scene_latincity" },
  { value: "cafe", label: "Café", langKey: "ai_scene_cafe" },
  { value: "asiancity", label: "Asian city", langKey: "ai_scene_asiancity" },
  { value: "nightlights", label: "Night lights", langKey: "ai_scene_nightlights" },
  { value: "desert", label: "Desert", langKey: "ai_scene_desert" },
];

const Lightbox = ({ src, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "zoom-out",
      }}
    >
      <img
        src={src}
        alt="AI enhanced full size"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw", maxHeight: "90vh",
          borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
          cursor: "default",
        }}
      />
      <button
        onClick={onClose}
        style={{
          position: "fixed", top: 20, right: 24,
          background: "rgba(255,255,255,0.2)",
          border: "1.5px solid rgba(255,255,255,0.4)",
          color: "#fff", fontSize: 22, lineHeight: 1,
          width: 40, height: 40, minWidth: 40, minHeight: 40,
          borderRadius: "50%", padding: 0, boxSizing: "border-box",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
        aria_label="Close"
      >×</button>
    </div>
  );
};

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Build a safe image URL by joining base + path without double-slashes.
 */
const buildImageUrl = (base, path) =>
  base.replace(/\/$/, "") + "/" + path.replace(/^\//, "");

/**
 * Fetch a remote URL via the Laravel proxy endpoint to avoid CORS.
 * Returns a File object.
 */
const fetchViaProxy = async (apiBaseUrl, token, remoteUrl, filename) => {
  const fd = new FormData();
  fd.append("image_url", remoteUrl);
  const res = await fetch(`${apiBaseUrl}products/product-image/proxy`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/jpeg" });
};

// ─── component ───────────────────────────────────────────────────────────────

const AiEnhancePanel = ({
  images,
  onReplaceImage,
  onAddImage,
  existingImages = [],
  apiBaseUrl,
  token,
  langJson,
}) => {
  const [sel, setSel] = useState(null);
  const [enhancing, setEnhancing] = useState(false);

  // aiResult stores { previewUrl (string — either blob URL or remote URL), remoteUrl }
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const [selectedModel, setSelectedModel] = useState("none");
  const [selectedBg, setSelectedBg] = useState("white");
  const [selectedScene, setSelectedScene] = useState("street");
  const [bgPrompt, setBgPrompt] = useState("");
  const [aiCredits, setAiCredits] = useState(0);

  const isVirtualModel = selectedModel === "female" || selectedModel === "male";
  const totalImages = (existingImages?.length ?? 0) + images.length;

  useEffect(() => {
    setSel(null);
    setAiResult(null);
    setAiError(null);
  }, [images.length]);

  useEffect(() => {
    if (token && apiBaseUrl) {
      fetchAiCredits();
    }
  }, [token, apiBaseUrl]);

  const fetchAiCredits = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}stores/ai-credits`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.success) setAiCredits(data.data.balance);
    } catch (e) {
      console.error("Error fetching AI credits:", e);
    }
  };

  if (totalImages === 0) return null;

  // ── callEnhance ────────────────────────────────────────────────────────────
  const callEnhance = async () => {
    if (!sel) return;
    setEnhancing(true);
    setAiResult(null);
    setAiError(null);
    setLightboxOpen(false);

    try {
      const fd = new FormData();
      fd.append("mode", selectedModel === "none" ? "beautify" : selectedModel);

      if (isVirtualModel) {
        fd.append("model_scene", selectedScene);
      } else {
        fd.append("background", selectedBg);
        if (bgPrompt.trim()) fd.append("background_prompt", bgPrompt.trim());
      }

      if (sel.source === "new") {
        // ✅ Local file — attach directly, no CORS issue
        fd.append("image", images[sel.idx]);
      } else {
        // ✅ Existing server image — send URL, Laravel fetches it server-side
        const remoteUrl = buildImageUrl(IMAGE_URL, existingImages[sel.idx].image);
        fd.append("image_url", remoteUrl);
      }

      const res = await fetch(`${apiBaseUrl}products/product-image/enhance`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();

      if (data.success && data.final_image_url) {
        // ✅ Use the remote URL directly for preview — no fetch(), no CORS
        setAiResult({ previewUrl: data.final_image_url, remoteUrl: data.final_image_url });
      } else {
        setAiError(data.message || langJson?.ai_error_failed || "Enhancement failed. Try again.");
      }
    } catch (err) {
      console.error("AI enhance error:", err);
      setAiError(langJson?.ai_error_network || "Network error during enhancement. Try again.");
    } finally {
      setEnhancing(false);
    }
  };

  // ── applyAiImage ───────────────────────────────────────────────────────────
  /**
   * Download the enhanced image via Laravel proxy (avoids CORS),
   * then call the provided callback with a File object.
   */
  const applyAiImage = async (callback) => {
    if (!sel || !aiResult) return;

    const filename =
      (sel.source === "new"
        ? images[sel.idx].name
        : existingImages[sel.idx].image.split("/").pop() || "image.jpg"
      ).replace(/\.[^.]+$/, "") + "_ai.jpg";

    try {
      // Always use Laravel proxy to avoid CORS issues across all domains
      const file = await fetchViaProxy(apiBaseUrl, token, aiResult.remoteUrl, filename);
      callback(file);
    } catch (err) {
      console.error("Failed to download AI image:", err);
      NotificationManager.error(langJson?.ai_error_download || "Failed to download AI image.");
    }
  };

  const handleUseAi = async () => {
    await applyAiImage((file) => {
      if (sel.source === "new") {
        onReplaceImage(sel.idx, file);
      } else {
        onAddImage(file);
      }
      resetAfterApply();
      NotificationManager.success(langJson?.ai_success_applied || "AI-enhanced image applied.");
    });
  };

  const handleAddAiImage = async () => {
    await applyAiImage((file) => {
      onAddImage(file);
      resetAfterApply();
      NotificationManager.success(langJson?.ai_success_added || "AI-enhanced image added.");
    });
  };

  const resetAfterApply = () => {
    setSel(null);
    setAiResult(null);
    setAiError(null);
    setLightboxOpen(false);
  };

  // ── misc handlers ──────────────────────────────────────────────────────────
  const handleSelect = (source, idx) => {
    setSel({ source, idx });
    setAiResult(null);
    setAiError(null);
    setLightboxOpen(false);
  };

  const handleRetry = () => {
    setAiResult(null);
    setAiError(null);
    callEnhance();
  };

  const handleModelChange = (e) => {
    setSelectedModel(e.target.value);
    setAiResult(null);
    setAiError(null);
  };

  const getOriginalPreviewSrc = () => {
    if (!sel) return null;
    if (sel.source === "new") return URL.createObjectURL(images[sel.idx]);
    return buildImageUrl(IMAGE_URL, existingImages[sel.idx].image);
  };

  const getOriginalAltText = () => {
    if (!sel) return "";
    if (sel.source === "new") return images[sel.idx].name;
    return existingImages[sel.idx].image.split("/").pop() || "image";
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {lightboxOpen && aiResult?.previewUrl && (
        <Lightbox src={aiResult.previewUrl} onClose={() => setLightboxOpen(false)} />
      )}

      <div className="ai-enhance-panel">

        <div className="ai-enhance-header" style={{ position: "relative" }}>
          <span className="ai-enhance-title">{langJson?.beautify_with_ai ?? "Beautify with AI"}</span>
          <div style={{
            position: "absolute",
            top: "50%",
            right: "0",
            transform: "translateY(-50%)",
            padding: "6px 12px",
            background: "#f0f2f5",
            border: "1px solid #d0d5dd",
            borderRadius: "4px",
            fontSize: "13px",
            fontWeight: "500",
            color: "#333",
            whiteSpace: "nowrap",
          }}>
            {langJson?.ai_credits || "AI Credits"}: <span style={{ color: aiCredits > 0 ? "#28a745" : "#dc3545", fontWeight: "bold" }}>{aiCredits}</span>
          </div>
        </div>

        <div className="ai-thumb-row">
          {existingImages.map((img, idx) => (
            <button
              key={`existing-${idx}`}
              type="button"
              className={`ai-thumb-btn ai-thumb-btn--existing${sel?.source === "existing" && sel?.idx === idx ? " selected" : ""}`}
              onClick={() => handleSelect("existing", idx)}
              title={img.image.split("/").pop()}
            >
              <img src={buildImageUrl(IMAGE_URL, img.image)} alt={img.image.split("/").pop()} />
            </button>
          ))}
          {images.map((img, idx) => (
            <button
              key={`new-${idx}`}
              type="button"
              className={`ai-thumb-btn${sel?.source === "new" && sel?.idx === idx ? " selected" : ""}`}
              onClick={() => handleSelect("new", idx)}
              title={img.name}
            >
              <img src={URL.createObjectURL(img)} alt={img.name} />
            </button>
          ))}
        </div>

        {!sel && (
          <p className="ai-select-hint">
            ↑ {langJson?.ai_select_image_hint ?? "Select an image above to preview AI enhancement"}
          </p>
        )}

        <div className="ai-controls-duo">
          <div className="ai-control-col">
            <label className="ai-control-label">{langJson?.ai_label_model ?? "Model"}</label>
            <select className="ai-select" value={selectedModel} onChange={handleModelChange}>
              {MODEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{langJson?.[o.langKey] ?? o.label}</option>
              ))}
            </select>
          </div>

          <div className="ai-control-col">
            {isVirtualModel ? (
              <>
                <label className="ai-control-label">{langJson?.ai_label_scene ?? "Scene"}</label>
                <select
                  className="ai-select"
                  value={selectedScene}
                  onChange={(e) => setSelectedScene(e.target.value)}
                >
                  {SCENE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{langJson?.[o.langKey] ?? o.label}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="ai-control-label">{langJson?.ai_label_background ?? "Background"}</label>
                <select
                  className="ai-select"
                  value={selectedBg}
                  onChange={(e) => setSelectedBg(e.target.value)}
                >
                  {BG_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{langJson?.[o.langKey] ?? o.label}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>

        {!isVirtualModel && (
          <div className="ai-control-row ai-control-row--full">
            <label className="ai-control-label">
              {langJson?.ai_label_custom_bg ?? "Custom background"}{" "}
              <span className="ai-optional">({langJson?.ai_optional ?? "optional"})</span>
            </label>
            <textarea
              className="ai-prompt-textarea"
              rows={2}
              maxLength={500}
              placeholder={langJson?.ai_bg_placeholder ?? "e.g. on a beach, in a Scandinavian interior, sunset rooftop terrace…"}
              value={bgPrompt}
              onChange={(e) => setBgPrompt(e.target.value)}
            />
            <div className="ai-prompt-footer">
              <span className="ai-prompt-hint">
                {langJson?.ai_bg_hint ?? "Leave blank to use the selected background automatically."}
              </span>
            </div>
          </div>
        )}

        {sel && (
          <div className="ai-compare-section">
            <div className="ai-compare-row">

              <div className="ai-compare-half">
                <div className="ai-compare-label">{langJson?.ai_compare_original ?? "Original"}</div>
                <div className="ai-compare-img-wrap">
                  <img src={getOriginalPreviewSrc()} alt={getOriginalAltText()} />
                </div>
              </div>

              <div className="ai-compare-half ai-compare-half--right">
                <div className="ai-compare-label ai-compare-label--ai">
                  {langJson?.ai_compare_enhanced ?? "AI enhanced"}
                </div>
                <div
                  className={`ai-compare-img-wrap${aiResult?.previewUrl && !enhancing ? " ai-compare-img-wrap--zoomable" : ""}`}
                  onClick={() => aiResult?.previewUrl && !enhancing && setLightboxOpen(true)}
                  title={aiResult?.previewUrl && !enhancing ? (langJson?.ai_click_enlarge ?? "Click to enlarge") : undefined}
                >
                  {enhancing && (
                    <div className="ai-loader-wrap">
                      <span className="ai-spinner" />
                      <span className="ai-loader-text">{langJson?.ai_enhancing ?? "Enhancing…"}</span>
                    </div>
                  )}
                  {!enhancing && aiResult?.previewUrl && (
                    <img src={aiResult.previewUrl} alt={langJson?.ai_compare_enhanced ?? "AI enhanced"} />
                  )}
                  {!enhancing && !aiResult?.previewUrl && (
                    <div className="ai-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                      <span>{langJson?.ai_click_to_preview ?? "Click Beautify to preview"}</span>
                    </div>
                  )}
                </div>
              </div>

            </div>

            {aiError && <p className="ai-error">{aiError}</p>}

            <div className="ai-action-row">
              {!aiResult?.previewUrl && !enhancing && (
                <button type="button" className="ai-btn ai-btn--primary" onClick={callEnhance}>
                  ✦ {langJson?.ai_beautify_btn ?? "Beautify this image"}
                </button>
              )}
              {aiResult?.previewUrl && !enhancing && (
                <>
                  <button type="button" className="ai-btn ai-btn--success" onClick={handleUseAi}>
                    ✓ {langJson?.ai_use_image_btn ?? "Use AI image"}
                  </button>
                  <button type="button" className="ai-btn ai-btn--add" onClick={handleAddAiImage}>
                    + {langJson?.ai_add_image_btn ?? "Add AI image"}
                  </button>
                  <button type="button" className="ai-btn ai-btn--secondary" onClick={handleRetry}>
                    ↺ {langJson?.ai_retry_btn ?? "Retry"}
                  </button>
                  <button type="button" className="ai-btn ai-btn--ghost" onClick={() => setSel(null)}>
                    {langJson?.ai_keep_original_btn ?? "Keep original"}
                  </button>
                </>
              )}
              {enhancing && (
                <button type="button" className="ai-btn ai-btn--disabled" disabled>
                  {langJson?.ai_enhancing ?? "Enhancing…"}
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default AiEnhancePanel;